import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AgricultureView } from "@/components/AgricultureView";
import { EarthquakeView } from "@/components/EarthquakeView";
import { AskAnalyst } from "@/components/AskAnalyst";
import { SatellitePicker } from "@/components/SatellitePicker";
import { FieldCaptures, captureSrc } from "@/components/FieldCaptures";
import { AlertRecipients } from "@/components/AlertRecipients";
import { ScanToolbar } from "@/components/ScanToolbar";
import { Loader2, Play, Cpu, Upload, FileCheck2, Sprout, Building2, ArrowLeft, ShieldCheck, Coins, Hash } from "lucide-react";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [preferQuantum, setPreferQuantum] = useState(false);
  const [stories, setStories] = useState(6);
  const [area, setArea] = useState(50);
  const [file, setFile] = useState(null);
  const [scene, setScene] = useState(null);
  const [active, setActive] = useState(null);
  const [captures, setCaptures] = useState(null);
  const loadCaptures = useCallback(() => api.get(`/field/captures?project_id=${id}`).then((r) => setCaptures(r.data)).catch(() => setCaptures([])), [id]);
  useEffect(() => { loadCaptures(); }, [loadCaptures]);

  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => {
    setData(r.data);
    if (r.data.scans.length) setActive((a) => (a && r.data.scans.find((s) => s.id === a.id)) || r.data.scans[0]);
  }).catch(() => toast.error("Failed to load project")), [id]);
  useEffect(() => { load(); }, [load]);

  const uploadFile = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.id;
  };

  const run = async () => {
    setRunning(true);
    try {
      let fileId = null;
      if (file) { toast.info("Uploading scene..."); fileId = await uploadFile(file); }
      toast.info(preferQuantum ? "Running Qiskit circuits + analysis + minting..." : "Running analysis + minting on-chain...");
      const sceneBody = scene ? { id: scene.id, datetime: scene.datetime, cloud_cover: scene.cloud_cover, vegetation_pct: scene.vegetation_pct, thumbnail: scene.thumbnail, bbox: scene.bbox, platform: scene.platform, red_href: scene.red_href, nir_href: scene.nir_href, blue_href: scene.blue_href, swir16_href: scene.swir16_href, refl_scale: scene.refl_scale, refl_offset: scene.refl_offset, aoi_bbox: scene.aoi_bbox } : null;
      const body = { prefer_quantum: preferQuantum, lang, file_id: fileId, scene: sceneBody, area_hectares: +area, stories: +stories };
      const { data: scan } = await api.post(`/projects/${id}/run`, body);
      toast.success(scan.alert ? "Analysis complete · contamination alert sent" : "Analysis complete · certificate minted");
      setActive(scan);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Analysis failed");
    } finally { setRunning(false); }
  };

  if (!data) return <div className="min-h-screen grid place-items-center bg-[#050A15]"><Loader2 className="h-7 w-7 animate-spin text-[#00FFFF]" /></div>;
  const { project, scans } = data;
  const isAgri = project.domain === "agriculture";

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#00FFFF] mb-5"><ArrowLeft className="h-4 w-4" /> Back</Link>

        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-sm" style={{ background: isAgri ? "#00FF7F1A" : "#F59E0B1A" }}>
            {isAgri ? <Sprout className="h-6 w-6 text-[#00FF7F]" /> : <Building2 className="h-6 w-6 text-[#F59E0B]" />}
          </span>
          <div>
            <h1 className="font-display font-bold text-2xl">{project.name}</h1>
            <p className="text-xs text-slate-500 capitalize font-mono">{project.domain} · {project.region || "—"}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {/* Control panel */}
          <div className="qs-panel p-6 h-fit lg:sticky lg:top-24">
            <h3 className="font-display font-semibold mb-4">Run Analysis</h3>

            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Data source</label>
            <label className="flex items-center gap-2 border border-dashed border-white/15 px-4 py-3 cursor-pointer hover:border-[#00FFFF]/50 transition-colors mb-1" data-testid="file-upload">
              <Upload className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-400 truncate">{file ? file.name : "Upload GeoTIFF / ENVI / .mat / image"}</span>
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])}
                accept=".tif,.tiff,.mat,.hdr,.img,.png,.jpg,.jpeg" />
            </label>
            <p className="text-[11px] text-slate-600 mb-4">Or pick a live Sentinel-2 scene below / leave empty for a synthetic tile.</p>

            {isAgri && <SatellitePicker selected={scene} onSelect={setScene} />}

            {isAgri ? (
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Area (hectares)</label>
                <input data-testid="area-input" type="number" value={area} onChange={(e) => setArea(e.target.value)}
                  className="w-full bg-[#050A15] border border-white/10 px-3 py-2 font-mono outline-none focus:border-[#00FFFF]" />
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Stories</label>
                <input data-testid="stories-input" type="number" min={1} max={40} value={stories} onChange={(e) => setStories(e.target.value)}
                  className="w-full bg-[#050A15] border border-white/10 px-3 py-2 font-mono outline-none focus:border-[#00FFFF]" />
              </div>
            )}

            <button data-testid="quantum-toggle" onClick={() => setPreferQuantum(!preferQuantum)}
              className={`w-full flex items-center justify-between px-4 py-3 border mb-5 transition-colors ${preferQuantum ? "border-[#8A2BE2] bg-[#8A2BE2]/10" : "border-white/10"}`}>
              <span className="flex items-center gap-2 text-sm"><Cpu className="h-4 w-4 text-[#8A2BE2]" /> Quantum backend</span>
              <span className={`text-xs font-mono ${preferQuantum ? "text-[#8A2BE2]" : "text-slate-500"}`}>{preferQuantum ? "QUANTUM (IBM QPU / Aer / fallback)" : "CLASSICAL"}</span>
            </button>

            <button data-testid="run-analysis-btn" onClick={run} disabled={running}
              className="qs-cta w-full flex items-center justify-center gap-2 bg-[#00FFFF] text-[#050A15] font-semibold py-3 disabled:opacity-60">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4" /> Run & Mint</>}
            </button>

            {isAgri && <AlertRecipients projectId={id} initial={project.alert_emails || []} />}

            {scans.length > 0 && (
              <div className="mt-6">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">History</p>
                <div className="space-y-1.5 max-h-64 overflow-auto">
                  {scans.map((s) => (
                    <button key={s.id} data-testid={`scan-${s.id}`} onClick={() => setActive(s)}
                      className={`w-full text-left px-3 py-2 border text-xs font-mono transition-colors ${active?.id === s.id ? "border-[#00FFFF] bg-[#00FFFF]/5" : "border-white/10 hover:border-white/25"}`}>
                      <div className="flex items-center gap-1.5 text-slate-300"><FileCheck2 className="h-3.5 w-3.5 text-[#00FF7F]" /> {new Date(s.created_at).toLocaleString()}</div>
                      <div className="text-slate-600 mt-0.5 truncate">{s.backend}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="lg:col-span-2">
            {!active ? (
              <div className="qs-panel p-16 text-center">
                <Play className="h-10 w-10 text-slate-600 mx-auto" />
                <p className="text-slate-400 mt-4">Run your first analysis to see results, on-chain certificate and AI report.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <ScanToolbar scan={active} />
                <OnChainBadges scan={active} />
                {isAgri ? <AgricultureView result={active.result} scanId={active.id} onUpdated={(n) => { setActive({ ...active, result: { ...active.result, nutrients: n } }); load(); }} /> : <EarthquakeView result={active.result} scanId={active.id} onUpdated={(scan) => { setActive(scan); load(); }} captures={(captures || []).map((c) => ({ ...c, photo_src: captureSrc(c) }))} />}
                <div className="qs-panel p-6" data-testid="ai-report">
                  <h4 className="font-display font-semibold mb-3 flex items-center gap-2"><Cpu className="h-4 w-4 text-[#00FFFF]" /> AI Analysis Report <span className="text-[10px] font-mono text-slate-500">Claude Sonnet 5</span></h4>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{active.report}</p>
                </div>
                <AskAnalyst projectId={id} scanId={active.id} domain={project.domain} />
              </div>
            )}
            <FieldCaptures projectId={id} list={captures} onChange={loadCaptures} showMap={isAgri || !active} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const OnChainBadges = ({ scan }) => {
  const cert = scan.certificate;
  const carbon = scan.carbon;
  const short = (h) => h ? `${h.slice(0, 10)}…${h.slice(-6)}` : "";
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="onchain-badges">
      {cert && (
        <div className="qs-grad-border p-5">
          <div className="flex items-center gap-2 text-[#00FFFF]"><ShieldCheck className="h-4 w-4" /> <span className="text-xs uppercase tracking-widest">{cert.standard} Certificate</span></div>
          <div className="font-mono text-xs text-slate-400 mt-3 space-y-1">
            <div className="flex items-center gap-1.5"><Hash className="h-3 w-3" /> token #{cert.token_id}</div>
            <div className="text-slate-500 break-all">tx {short(cert.tx_hash)}</div>
            <div className="text-slate-600">block {cert.block} · {cert.network}</div>
          </div>
        </div>
      )}
      {carbon && (
        <div className="qs-grad-border p-5">
          <div className="flex items-center gap-2 text-[#00FF7F]"><Coins className="h-4 w-4" /> <span className="text-xs uppercase tracking-widest">{carbon.standard} · {carbon.symbol}</span></div>
          <div className="font-mono text-xs text-slate-400 mt-3 space-y-1">
            <div className="text-lg text-[#00FF7F] font-bold">{carbon.amount} ZCC</div>
            <div className="text-slate-500 break-all">tx {short(carbon.tx_hash)}</div>
            <div className="text-slate-600">block {carbon.block} · {carbon.network}</div>
          </div>
        </div>
      )}
    </div>
  );
};
